import React, { useState, useEffect } from 'react';
import { Button, toast } from '@ponzinomics/ui';
import { useVerifySolanaPayment } from '../hooks/use-billing';
import type { SolanaPaymentResult } from '../lib/billing-api';

function formatCredits(n: number): string {
  return n.toLocaleString();
}

interface SolanaPayModalProps {
  payment: SolanaPaymentResult;
  onClose: () => void;
}

export function SolanaPayModal({ payment, onClose }: SolanaPayModalProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const [verified, setVerified] = useState(false);
  const verify = useVerifySolanaPayment();

  useEffect(() => {
    if (verified) return;

    const interval = setInterval(async () => {
      try {
        const result = await verify.mutateAsync(payment.reference);
        if (result.verified) {
          setVerified(true);
          clearInterval(interval);
          toast({ title: `${formatCredits(result.credits ?? 0)} credits added to your account!` });
        }
      } catch {
        // keep polling silently
      }
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.reference, verified]);

  const handleCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(payment.recipient);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md space-y-5 rounded-xl border border-white/[0.08] bg-gray-950 p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Pay with USDC</h2>
          <button
            onClick={onClose}
            className="text-gray-500 transition-colors hover:text-white"
            aria-label="Close payment modal"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {verified ? (
          /* Success state */
          <div className="space-y-3 py-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
              <svg
                className="h-7 w-7 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-semibold text-white">Payment confirmed!</p>
            <p className="text-sm text-gray-400">Credits have been added to your account.</p>
            <Button onClick={onClose} className="mt-2 w-full">
              Close
            </Button>
          </div>
        ) : (
          /* Pending state */
          <>
            <div className="space-y-4 rounded-lg border border-white/[0.06] bg-white/[0.03] p-4">
              <div>
                <p className="mb-1 text-xs text-gray-500">Amount due</p>
                <p className="font-semibold text-white">${payment.amount.toFixed(2)} USDC</p>
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500">Recipient address (Solana)</p>
                <div className="flex items-start gap-2">
                  <code className="flex-1 break-all font-mono text-xs leading-relaxed text-gray-300">
                    {payment.recipient}
                  </code>
                  <button
                    onClick={handleCopy}
                    className="shrink-0 rounded bg-white/[0.06] px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-white/[0.1] hover:text-white"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <Button className="w-full" onClick={() => window.open(payment.solanaPay, '_blank')}>
              Open in Wallet
            </Button>

            <p className="text-center text-xs text-gray-500">
              Send exactly ${payment.amount.toFixed(2)} USDC on the Solana network to the address
              above.
            </p>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="bg-brand-400 h-1.5 w-1.5 animate-pulse rounded-full" />
              Waiting for payment confirmation...
            </div>
          </>
        )}
      </div>
    </div>
  );
}
